import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Button } from "src/components/generic";
import { stripe as stripeUtils } from "src/utils";
import type { SubscriptionSummary } from "src/utils/stripe/subscription";

interface IBillingInnerProps {
  customerId: string;
  subscription: SubscriptionSummary | null;
  paymentMethodLast4: string | null;
  clientSecret: string;
  accountType: string;
  onChanged: () => Promise<void>;
};

type PlanOption = {
  label: string;
  priceId: string;
  amountDisplay: string;
  interval: string;
};

const BillingInner: FunctionComponent<IBillingInnerProps> = ({ clientSecret, accountType,  customerId, subscription, paymentMethodLast4, onChanged }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string>("");
  const [err, setErr] = useState<string>("");
  const [subscriptions, setSubscriptions] = useState([]);

  const plans: PlanOption[] = useMemo(
    () => subscriptions,
    [subscriptions]
  );

  const currentPriceId = subscription?.priceId;

  const formatDate = (unixSeconds?: number) => {
    if (!unixSeconds) return "";
    return new Date(unixSeconds * 1000).toLocaleDateString();
  };

  const confirmCardAndGetPaymentMethodId = async (): Promise<string> => {
    if (!stripe || !elements) throw new Error("Stripe is not ready.");
    const card = elements.getElement(CardElement);
    if (!card) throw new Error("Card element not found.");

    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card },
    } as any);

    if (result.error) throw new Error(result.error.message || "Card setup failed.");
    const pm = (result.setupIntent as any)?.payment_method;
    if (!pm) throw new Error("Missing payment_method on SetupIntent.");
    return typeof pm === "string" ? pm : pm.id;
  };

  const handleSubscribe = async (priceId: string) => {
    setErr("");
    setMsg("");
    setBusy(true);

    try {
      const paymentMethodId = await confirmCardAndGetPaymentMethodId();
      await stripeUtils.payments.subscribeUser(customerId, priceId, paymentMethodId);
      setMsg("Subscribed successfully.");
      await onChanged();
    } catch (e: any) {
      setErr(e?.message || "Subscription failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleUpgrade = async (newPriceId: string) => {
    if (!subscription?.id) return;

    setErr("");
    setMsg("");
    setBusy(true);

    try {
      // Many businesses do NOT require a new card for upgrade.
      // If you *do* want to ensure a card exists, confirmCardAndGetPaymentMethodId() first.
      await stripeUtils.subscription.upgradeSubscription(subscription.id, newPriceId);
      setMsg("Plan updated.");
      await onChanged();
    } catch (e: any) {
      setErr(e?.message || "Upgrade failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!subscription?.id) return;

    setErr("");
    setMsg("");
    setBusy(true);

    try {
      await stripeUtils.subscription.cancelSubscription(subscription.id, true);
      setMsg("Cancellation scheduled at period end.");
      await onChanged();
    } catch (e: any) {
      setErr(e?.message || "Cancel failed.");
    } finally {
      setBusy(false);
    }
  };

  const hasActiveSub = subscription && (subscription.status === "active" || subscription.status === "trialing");

  const fetchSubscriptionPlans = async () => {
    try {
      const result = await stripeUtils.subscription.subscriptions();

      const subs = result.plans.map((subscription: any) => {
        console.log({accountType})
        if((accountType === 'Client' || accountType === 'Owner') && subscription.label.includes('Client')) return {
          label: subscription.label, 
          priceId: subscription.priceId, 
          amountDisplay: `$${(subscription.amount / 100).toFixed(2)}`, 
          interval: "month"
        }
        else if (accountType === 'Company' && subscription.label.includes('Enterprise')) return {
          label: subscription.label, 
          priceId: subscription.priceId, 
          amountDisplay: `$${(subscription.amount / 100).toFixed(2)}`, 
          interval: "month"
        } 
        else return null
      }).filter((item: any) => item !== null)
      console.log(subs)
      setSubscriptions(subs);
    } catch(error: any) {
      setErr(error?.message || "Failed to Fetch Plans")
    }
  }

  useEffect(() => {
    if(!subscriptions.length) fetchSubscriptionPlans();
  }, []);
  console.log({plans})
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h3 className="text-lg font-semibold">Billing</h3>
        {paymentMethodLast4 ? (
          <p className="text-sm opacity-80">Card on file: •••• {paymentMethodLast4}</p>
        ) : (
          <p className="text-sm opacity-80">No card on file yet.</p>
        )}
      </div>

      {err && <p className="text-red-600">{err}</p>}
      {msg && <p className="text-green-700">{msg}</p>}

      {/* Current subscription summary */}
      <div className="rounded border p-4 space-y-2">
        <div className="flex items-center justify-between">
          <p className="font-medium">Current subscription</p>
          <span className="text-sm opacity-80">{subscription?.status ?? "none"}</span>
        </div>

        {subscription ? (
          <>
            <p className="text-sm opacity-80">Renews / ends on: {formatDate(subscription.current_period_end)}</p>
            {subscription.cancel_at_period_end && (
              <p className="text-sm text-amber-700">Cancellation is scheduled for period end.</p>
            )}

            {hasActiveSub && (
              <Button styles="w-[200px]" text={busy ? "Working..." : "Cancel at period end"} disabled={busy} onClick={handleCancel} />
            )}
          </>
        ) : (
          <p className="text-sm opacity-80">You do not currently have an active subscription.</p>
        )}
      </div>

      {/* Card entry (only needed for subscribe or if you want to support “update card”) */}
      <div className="rounded border p-4 space-y-3">
        <p className="font-medium">Payment method</p>
        <div className="rounded border p-3">
          <CardElement options={{ hidePostalCode: true }} />
        </div>
        <p className="text-xs opacity-70">
          Your card is saved securely with Stripe.
        </p>
      </div>

      {/* Plans */}
      <div className="space-y-3">
        <p className="font-medium">Plans</p>

        <div className="grid gap-3 md:grid-cols-3">
          {plans.map((p) => {
            const isCurrent = currentPriceId === p.priceId;
            return (
              <div key={p.priceId} className="rounded border p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="font-semibold">{p.label}</p>
                  <span className="text-sm opacity-80">
                    {p.amountDisplay}/{p.interval}
                  </span>
                </div>

                {isCurrent && <p className="text-sm text-green-700">Current plan</p>}

                {!subscription && (
                  <Button text={busy ? "Working..." : "Subscribe"} disabled={busy} onClick={() => handleSubscribe(p.priceId)} />
                )}

                {subscription && !isCurrent && hasActiveSub && (
                  <Button styles="w-[200px]" text={busy ? "Working..." : "Switch to this plan"} disabled={busy} onClick={() => handleUpgrade(p.priceId)} />
                )}

                {subscription && !hasActiveSub && (
                  <Button text={busy ? "Working..." : "Subscribe"} disabled={busy} onClick={() => handleSubscribe(p.priceId)} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BillingInner;
